"use client";

import { createContext, useContext, useState, useEffect } from 'react';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { createClient } from '@/lib/supabase/client';
import { loginWithPassword, verifyCaptcha } from '@/actions/auth';
import { friendlyAuthError } from '@/lib/authErrors';
import { clearStaffShellCache } from '@/lib/perf/staffShellCache';
import { clearNotifCountCache } from '@/lib/perf/notifCountCache';
import { markAuthProfile, resetShellRecorder } from '@/lib/perf/shellChain';

interface User {
  id: string;
  vid: string;
  firstName: string;
  lastName: string;
  role: string;
  email: string;
  [key: string]: unknown;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  loginWithEmail: (email: string, password: string, captchaToken?: string) => Promise<{ success: boolean; role?: string; error?: string }>;
  loginWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  sendPasswordReset: (email: string, captchaToken?: string) => Promise<{ success: boolean; error?: string }>;
  updateEmail: (newEmail: string) => Promise<{ success: boolean; error?: string }>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
}

let _supabase: ReturnType<typeof createClient> | null = null;
function getSupabase() {
  if (!_supabase) _supabase = createClient();
  return _supabase;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const supabase = getSupabase();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event: AuthChangeEvent, session: Session | null) => {
      if (event === 'SIGNED_OUT') {
        clearStaffShellCache();
        clearNotifCountCache();
        resetShellRecorder();
        setUser(null);
        setLoading(false);
        return;
      }
      if (session?.user) {
        const profile = await fetchProfile(session.user.id);
        // Only clear user on explicit sign-out, not transient profile fetch failures
        if (profile) {
          setUser({ ...profile, email: session.user.email ?? '' });
          markAuthProfile(profile.role);
        }
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  async function fetchProfile(userId: string): Promise<User | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, vid, first_name, last_name, role, is_superuser')
      .eq('id', userId)
      .single();
    if (error) console.error('fetchProfile failed:', error.message);
    if (!data) return null;
    return {
      id: data.id,
      vid: data.vid ?? '',
      firstName: data.first_name,
      lastName: data.last_name,
      role: data.role,
      isSuperuser: data.is_superuser === true,
      email: '',
    };
  }

  // Cookie-safe path: Server Action writes the session via next/headers cookies,
  // then the login page hard-navigates. Do not call browser signInWithPassword
  // here — that races proxy.ts (confirmed live 2026-08-26).
  async function loginWithEmail(email: string, password: string, captchaToken?: string) {
    try {
      return await loginWithPassword(email, password, captchaToken);
    } catch (err) {
      console.error('loginWithEmail failed:', err);
      return { success: false, error: 'Something went wrong signing in. Please try again.' };
    }
  }

  // Browser navigates to Google, then Supabase, then /auth/callback.
  // The callback refuses anyone without a staff-created profiles row.
  async function loginWithGoogle() {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: { prompt: 'select_account' },
      },
    });
    if (error) return { success: false, error: error.message };
    return { success: true };
  }

  async function sendPasswordReset(email: string, captchaToken?: string) {
    if (captchaToken) {
      const { success } = await verifyCaptcha(captchaToken);
      if (!success) return { success: false, error: 'CAPTCHA verification failed. Please try again.' };
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/set-password`,
    });
    if (error) return { success: false, error: error.message };
    return { success: true };
  }

  // Supabase requires confirming a new email before it takes effect (sends a
  // confirmation link to the new address; some configs also require
  // re-confirming from the old one) — this call starts that flow, it doesn't
  // change the email immediately.
  async function updateEmail(newEmail: string) {
    const { error } = await supabase.auth.updateUser({ email: newEmail });
    if (error) return { success: false, error: error.message };
    return { success: true };
  }

  // Supabase's "Require current password when changing password" Auth setting
  // (if enabled on the project) validates current_password server-side before
  // accepting the new one — this call doesn't re-verify it client-side, GoTrue does.
  async function changePassword(currentPassword: string, newPassword: string) {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
      current_password: currentPassword,
    });
    if (error) return { success: false, error: friendlyAuthError(error.message) };
    return { success: true };
  }

  // scope: 'local' — signs this tab/device out only. The default ('global')
  // revokes every session for the user everywhere, which meant logging out in
  // one browser tab silently killed the session in every other open tab too
  // (they'd only notice on their next auto-refresh/focus check, and could then
  // pick up whatever session state cookies happened to hold at that moment —
  // reported as confusing multi-tab desync 2026-08-18). This app has no
  // separate "log out of all devices" action; if one gets added later, that's
  // the place for scope: 'global' or 'others', not the everyday logout button.
  async function logout() {
    clearStaffShellCache();
    clearNotifCountCache();
    resetShellRecorder();
    await supabase.auth.signOut({ scope: 'local' });
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, loginWithEmail, loginWithGoogle, sendPasswordReset, updateEmail, changePassword, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
